import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { CalendarIcon, FileText, Plus, Search, Upload } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useJournals } from "~/hooks/contabilidad/useJournals";
import {
  useCreateMovement,
  useDeleteMovement,
  useMovements,
  usePublishMovement,
  useUpdateMovement,
} from "~/hooks/contabilidad/useMovements";
import { useReturnSupplierPaymentDraft } from "~/hooks/purchasing/useSupplierPayments";
import { cn } from "~/lib/utils";
import { exportMovimientoToExcel } from "~/services/contabilidad/movimientoExportService";
import { movementService } from "~/services/contabilidad/movementService";
import type { AccountMovement, MovementFormData } from "~/types/contabilidad";

import { MovimientoDialog } from "./components/MovimientoDialog";
import { MovimientoImportDialog } from "./components/MovimientoImportDialog";
import { MovimientoTable } from "./components/MovimientoTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

// Comprobantes filters persist for the browser-tab session so that leaving the
// list (e.g. opening a comprobante to publish it) and coming back restores the
// exact same view — status card + date range + company/journal/search —
// instead of resetting to the "Total" card and the current-month default.
const FILTERS_STORAGE_KEY = "comprobantes-contables-filters";

interface PersistedFilters {
  search: string;
  companyFilter: string;
  journalFilter: string;
  statusFilter: string;
  dateFrom?: string;
  dateTo?: string;
}

function loadPersistedFilters(): PersistedFilters | null {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedFilters) : null;
  } catch {
    return null;
  }
}

export default function MovimientosPage() {
  const [persistedFilters] = useState<PersistedFilters | null>(() =>
    loadPersistedFilters()
  );
  const [search, setSearch] = useState(persistedFilters?.search ?? "");
  const [companyFilter, setCompanyFilter] = useState<string>(
    persistedFilters?.companyFilter ?? "all"
  );
  const [journalFilter, setJournalFilter] = useState<string>(
    persistedFilters?.journalFilter ?? "all"
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    persistedFilters?.statusFilter ?? "all"
  );
  const [dateFrom, setDateFrom] = useState<Date | undefined>(() =>
    persistedFilters
      ? persistedFilters.dateFrom
        ? dayjs(persistedFilters.dateFrom).toDate()
        : undefined
      : dayjs().startOf("month").toDate()
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(() =>
    persistedFilters
      ? persistedFilters.dateTo
        ? dayjs(persistedFilters.dateTo).toDate()
        : undefined
      : dayjs().toDate()
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingMovement, setEditingMovement] =
    useState<AccountMovement | null>(null);

  const trimmedSearch = search.trim();
  const hasSearch = trimmedSearch.length > 0;

  // When searching, ignore the date window so a comprobante can be found by its
  // número de asiento / referencia regardless of its date. The date range is
  // only a browsing default, not a constraint on search.
  const filters = {
    ...(companyFilter !== "all" && { company_id: companyFilter }),
    ...(journalFilter !== "all" && { journal_id: journalFilter }),
    ...(!hasSearch &&
      dateFrom && { date_from: dayjs(dateFrom).format("YYYY-MM-DD") }),
    ...(!hasSearch &&
      dateTo && { date_to: dayjs(dateTo).format("YYYY-MM-DD") }),
  };

  const { data: movements = [], isLoading } = useMovements(filters);
  const { data: companies = [] } = useCompanies();
  const { data: journals = [] } = useJournals();

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
  }, [companies]);

  // Persist filters so returning to the list (e.g. after publishing a borrador
  // from its detail page) keeps the same status card and date range intact.
  useEffect(() => {
    try {
      const data: PersistedFilters = {
        search,
        companyFilter,
        journalFilter,
        statusFilter,
        dateFrom: dateFrom ? dayjs(dateFrom).format("YYYY-MM-DD") : undefined,
        dateTo: dateTo ? dayjs(dateTo).format("YYYY-MM-DD") : undefined,
      };
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage write failures (e.g. private mode / quota).
    }
  }, [search, companyFilter, journalFilter, statusFilter, dateFrom, dateTo]);
  const createMutation = useCreateMovement();
  const updateMutation = useUpdateMovement();
  const deleteMutation = useDeleteMovement();
  const publishMutation = usePublishMovement();
  const returnToSupplierPaymentsMutation = useReturnSupplierPaymentDraft();

  const searchedMovements = movements.filter((movement) => {
    if (!trimmedSearch) return true;
    const searchLower = trimmedSearch.toLowerCase();
    return (
      movement.move_number.toLowerCase().includes(searchLower) ||
      movement.ref.toLowerCase().includes(searchLower) ||
      movement.journal?.name?.toLowerCase().includes(searchLower) ||
      movement.company?.name?.toLowerCase().includes(searchLower)
    );
  });

  const filteredMovements = searchedMovements.filter((movement) => {
    if (statusFilter === "published" && !movement.is_published) return false;
    if (statusFilter === "draft" && movement.is_published) return false;
    return true;
  });

  const handleCreate = (data: MovementFormData) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setDialogOpen(false);
      },
    });
  };

  const handleImport = (data: MovementFormData) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setImportDialogOpen(false);
      },
    });
  };

  const handleEdit = async (movement: AccountMovement) => {
    // Fetch movement with lines before editing
    const movementWithLines = await movementService.getWithLines(movement.id);
    setEditingMovement(movementWithLines);
    setDialogOpen(true);
  };

  const handleUpdate = (data: MovementFormData) => {
    if (editingMovement) {
      updateMutation.mutate(
        { id: editingMovement.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingMovement(null);
          },
        }
      );
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handlePublish = (id: string) => {
    // Publishing a borrador must NOT touch the active filters: the status
    // filter stays on "Borradores" and the date range is kept, so the user
    // stays exactly where they were. Only the published comprobante drops out
    // of the list (it's no longer a borrador).
    publishMutation.mutate(id);
  };

  const handleReturnToSupplierPayments = (id: string) => {
    returnToSupplierPaymentsMutation.mutate(id);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMovement(null);
  };

  const handleDownload = async (movement: AccountMovement) => {
    const movementWithLines = await movementService.getWithLines(movement.id);
    exportMovimientoToExcel({
      movement: movementWithLines,
      lines: movementWithLines.lines || [],
    });
  };

  // Stats — reflect the active search so the counts match what the table shows,
  // but stay independent of the status filter (the cards toggle that filter).
  const totalMovements = searchedMovements.length;
  const publishedCount = searchedMovements.filter((m) => m.is_published).length;
  const draftCount = searchedMovements.filter((m) => !m.is_published).length;

  return (
    <div className="container mx-auto w-[80vw] space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            Comprobantes Contables
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            className="rounded-full"
          >
            <Upload className="mr-2 h-4 w-4" />
            Importar
          </Button>
          <Button onClick={() => setDialogOpen(true)} className="rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Comprobante
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card
          onClick={() => setStatusFilter("all")}
          className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            statusFilter === "all" && "ring-2 ring-primary"
          )}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMovements}</div>
          </CardContent>
        </Card>
        <Card
          onClick={() => setStatusFilter("published")}
          className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            statusFilter === "published" && "ring-2 ring-green-500"
          )}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Publicados</CardTitle>
            <FileText className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {publishedCount}
            </div>
          </CardContent>
        </Card>
        <Card
          onClick={() => setStatusFilter("draft")}
          className={cn(
            "cursor-pointer transition-all hover:shadow-md",
            statusFilter === "draft" && "ring-2 ring-yellow-500"
          )}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Borradores</CardTitle>
            <FileText className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {draftCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por N° de asiento, referencia, diario o empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Todas las empresas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las empresas</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={journalFilter} onValueChange={setJournalFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Todos los diarios" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los diarios</SelectItem>
            {journals.map((journal) => (
              <SelectItem key={journal.id} value={journal.id}>
                {journal.code} - {journal.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="published">Publicados</SelectItem>
            <SelectItem value="draft">Borradores</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[140px] justify-start text-left font-normal",
                !dateFrom && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateFrom ? dayjs(dateFrom).format("DD/MM/YYYY") : "Desde"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateFrom}
              onSelect={setDateFrom}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-[140px] justify-start text-left font-normal",
                !dateTo && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateTo ? dayjs(dateTo).format("DD/MM/YYYY") : "Hasta"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateTo}
              onSelect={setDateTo}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Table */}
      <MovimientoTable
        movements={filteredMovements}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPublish={handlePublish}
        onDownload={handleDownload}
        onReturnToSupplierPayments={handleReturnToSupplierPayments}
      />

      {/* Dialog */}
      <MovimientoDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        movement={editingMovement ?? undefined}
        onSubmit={editingMovement ? handleUpdate : handleCreate}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Import Dialog */}
      <MovimientoImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSubmit={handleImport}
        isLoading={createMutation.isPending}
      />
    </div>
  );
}
